import React from "react";
import { Chip } from "@mui/material";
import Colors from "Constants/Color";

interface StatusChipProps {
  color?: string;       // optional: any color: "#43a047", "blue", "rgb(30,136,229)"
  status?: string;      // optional: status text for auto color detection
  children?: React.ReactNode; // optional: label or JSX (will use status if not provided)
  size?: "small" | "medium";
  variant?: "outlined" | "filled";
}

// Status-based color mapping for dynamic color detection
const getStatusColorMap = (status: string): { bg: string; color: string; border: string } => {
  // const lowerStatus = status?.toLowerCase() || "";
  const lowerStatus = status?.trim().toLowerCase() || "";


  // Dispatch status colors
  if (lowerStatus === "completed" || lowerStatus === "complete") {
    return {
      bg: Colors.rnet_light_green_pastel || "#CBF4C9",
      color: Colors.rnet_dark_green_pastel || "#64B161",
      border: Colors.rnet_dark_green_pastel || "#64B161",
    };
  }
  if (lowerStatus === "delivered") {
    return {
      bg: "#D1F2EB", // Light teal/cyan
      color: "#0C7C59", // Dark teal
      border: "#0C7C59",
    };
  }
  if (lowerStatus === "pos") {
    return {
      bg: Colors.rnet_light_plural || "#F2EBFF",
      color: Colors.rnet_dark_plural || "#A677FD",
      border: Colors.rnet_dark_plural || "#A677FD",
    };
  }
  if (lowerStatus === "order") {
    return {
      bg: "#FFE5CC", // Light orange
      color: "#CC6600", // Dark orange
      border: "#CC6600",
    };
  }
  if (lowerStatus === "requested for dispatch") {
    return {
      bg: Colors.rnet_light_blue || "#C9DEF4",
      color: Colors.rnet_dark_blue || "#0C437F",
      border: Colors.rnet_dark_blue || "#0C437F",
    };
  }
  if (lowerStatus === "dispatched" || lowerStatus === "created") {
    return {
      bg: Colors.rnet_light_plural || "#F2EBFF",
      color: Colors.rnet_dark_plural || "#A677FD",
      border: Colors.rnet_dark_plural || "#A677FD",
    };
  }
  if (lowerStatus === "pending" || lowerStatus === "pending dispatch") {
    return {
      bg: Colors.rnet_light_yellow || "#fff6CD",
      color: Colors.rnet_dark_yellow || "#9A6800",
      border: Colors.rnet_dark_yellow || "#9A6800",
    };
  }
  if (lowerStatus === "cancelled" || lowerStatus === "canceled") {
    return {
      bg: Colors.rnet_light_red || "#F4C9C9",
      color: Colors.rnet_dark_red || "#843333",
      border: Colors.rnet_dark_red || "#843333",
    };
  }
  if (lowerStatus === "returned") {
    return {
      bg: Colors.rnet_light_red || "#F4C9C9",
      color: Colors.rnet_dark_red || "#843333",
      border: Colors.rnet_dark_red || "#843333",
    };
  }
  if (lowerStatus === "partial returned") {
    return {
      bg: Colors.rnet_light_yellow || "#fff6CD",
      color: Colors.rnet_dark_yellow || "#9A6800",
      border: Colors.rnet_dark_yellow || "#9A6800",
    };
  }
  if (lowerStatus === "no return") {
    return {
      bg: Colors.rnet_light_green_pastel || "#CBF4C9",
      color: Colors.rnet_dark_green_pastel || "#64B161",
      border: Colors.rnet_dark_green_pastel || "#64B161",
    };
  }
  if (lowerStatus === "in progress" || lowerStatus === "processing") {
    return {
      bg: Colors.rnet_light_blue || "#C9DEF4",
      color: Colors.rnet_dark_blue || "#0C437F",
      border: Colors.rnet_dark_blue || "#0C437F",
    };
  }

  // 1. Pending
if (lowerStatus === "pending") {
  return {
    bg: "#FFF6CD",       // soft yellow
    color: "#9A6800",    // dark yellow
    border: "#9A6800",
  };
}

// 2. Site Visit Completed
if (lowerStatus === "site visit completed") {
  return {
    bg: "#CBF4C9",       // soft green
    color: "#3C9142",    // dark green
    border: "#3C9142",
  };
}

// 3. Quotation Created
if (lowerStatus === "quotation created") {
  return {
    bg: "#E0F2FF",       // light sky blue
    color: "#0C7BB3",    // dark sky blue
    border: "#0C7BB3",
  };
}

// 4. Request for Quotation
if (lowerStatus === "request for quotation") {
  return {
    bg: "#E3E8FF",        // light indigo
    color: "#3F51B5",     // dark indigo
    border: "#3F51B5",
  };
}

  

  // Default grey
  return {
    bg: "#F5F5F5",
    color: "#757575",
    border: "#E0E0E0",
  };
};

const getSoftBackgroundColor = (color: string): string => {
  try {
    // Create a temporary element to let browser parse the color
    const temp = document.createElement("div");
    temp.style.color = color;

    // Get computed rgb values
    document.body.appendChild(temp);
    const rgb = getComputedStyle(temp).color;
    document.body.removeChild(temp);

    const [r, g, b] = rgb
      .replace("rgb(", "")
      .replace(")", "")
      .split(",")
      .map((v) => parseInt(v.trim(), 10));

    // Enhanced lighten logic with better opacity
    return `rgba(${r}, ${g}, ${b}, 0.12)`; 
  } catch {
    // fallback grey tone
    return "rgba(0,0,0,0.08)";
  }
};

const StatusChip: React.FC<StatusChipProps> = ({ 
  color, 
  status, 
  children, 
  size = "small",
  variant = "filled"
}) => {
  // Determine colors: priority: color prop > status-based > default
  let finalColor: string;
  let bgColor: string;
  let borderColor: string;

  if (color) {
    // Use provided color
    finalColor = color;
    bgColor = getSoftBackgroundColor(color);
    borderColor = color;
  } else if (status) {
    // Auto-detect from status
    const statusColors = getStatusColorMap(status);
    finalColor = statusColors.color;
    bgColor = statusColors.bg;
    borderColor = statusColors.border;
  } else {
    // Default grey
    finalColor = "#757575";
    bgColor = "#F5F5F5";
    borderColor = "#E0E0E0";
  }

  const isOutlined = variant === "outlined";
  const isSmall = size === "small";

  // Determine label: children > status (capitalized) > "-"
  // const label = children 
  //   ? children 
  //   : status 
  //   ? status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()
  //   : "-";
  const rawLabel = children || status || "-";

const label = rawLabel
  ?.toString()
  .trim()
  .replace(/\s+/g, " "); // remove extra spaces

  return (
    <Chip
      label={label}
      style={{
        background: isOutlined ? "transparent" : bgColor,
        color: finalColor,
        border: `1.5px solid ${borderColor}`,
        height: isSmall ? "28px" : "32px",
        fontSize: isSmall ? "11px" : "12px",
        padding: isSmall ? "0 10px" : "0 12px",
        borderRadius: "16px",
        fontWeight: 500,
        fontFamily: `"Poppins", sans-serif`,
        textTransform: "capitalize",
        transition: "all 0.2s ease",
        boxShadow: isOutlined ? "none" : "0 1px 2px rgba(0,0,0,0.05)",
      }}
      size={size}
      variant={isOutlined ? "outlined" : "filled"}
    />
  );
};

export default StatusChip;
